import { useState, useEffect } from "react";
import { API } from "aws-amplify";
import CircularProgress from "@mui/material/CircularProgress";
import {
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  Card,
  Container,
  DialogTitle,
  Tooltip,
  IconButton,
  TableRow,
  Button,
  Alert,
} from "@mui/material";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from "react-router-dom";
import InfoAlert from "./helpers/InfoAlert";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import { InfoOutlined } from '@mui/icons-material';
import EnergyClassA from "./svgr/EnergyClasses/EnergyClassA";
import EnergyClassB from "./svgr/EnergyClasses/EnergyClassB";
import EnergyClassC from "./svgr/EnergyClasses/EnergyClassC";
import EnergyClassD from "./svgr/EnergyClasses/EnergyClassD";
import EnergyClassE from "./svgr/EnergyClasses/EnergyClassE";
import EnergyClassF from "./svgr/EnergyClasses/EnergyClassF";
import EnergyClassG from "./svgr/EnergyClasses/EnergyClassG";

export function Equipment({ serialNumber, non_epta, tagId }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const tableData = [
    { label: "Equipment", key: "equipment_number" },
    { label: "Serial Number", key: "serial_number" },
    { label: "Description", key: "description" },
    { label: "Store ID", key: "store_id" },
    { label: "Producer", key: "manufacturer" },
    { label: "Year of construction", key: "serial_number_creation_year" },
    { label: "Energy Label", key: "energy_label" },
  ];
  const [equipment, setEquipment] = useState([]);

  useEffect(() => {
    const redirectTo = (storeId, sn) => {
      const envHostname = window.location.hostname.split('.')[0];
      if(envHostname === 'eprel' || envHostname === 'prod' || envHostname === 'nfc') {
        const url = `https://www.myepta.com/store/${storeId}/equipment/${sn}/assetID`;
        window.location.href = url;
      } else {
        const url = `http://qa.d1is8x522gcrfb.amplifyapp.com/store/${storeId}/equipment/${sn}/assetID`;
        window.location.href = url;
      }
    };

    const fetchData = async () => {
      try {
        const payload = {
          queryStringParameters: {
            serial_number: serialNumber,
          },
        };
        const data = await API.post("NFClandingAPI", "/getNFCEquipment", payload);
        setEquipment(data);
        if (data.length > 0 && data[0].serial_number && data[0].store_id) {
          redirectTo(data[0].store_id, data[0].serial_number);
        } else if (data === null || data.length === 0) {
          setError("missing-id");
          setLoading(false);
        } else {
          setError("invalid-data");
          setLoading(false);
        }
      } catch (err) {
        console.error("Error fetching equipment data:", err);
        setError("api-error");
        setLoading(false);
      }
    };

    if (non_epta) {
      redirectTo("2184166", non_epta.serial_number);
    } else if (serialNumber) {
      fetchData();
    } else {
      setError("missing-serial");
      setLoading(false);
    }
  }, [serialNumber, non_epta]);

  // Loading state
  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          width: "100vw",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Error states
  if (error) {
    let content;

    switch (error) {
      case "missing-serial":
        content = (
          <>
            <Typography variant="h5" gutterBottom>
              <Tooltip title="Go back">
                <IconButton onClick={() => navigate(`/`)} sx={{ mr: 0.5 }}>
                  <ArrowBackIcon />
                </IconButton>
              </Tooltip>
              Serial Number Missing
            </Typography>
            <Typography variant="body1" gutterBottom>
              We apologize, but the serial number for this asset with tag id {tagId.toUpperCase()} is currently unavailable.
            </Typography>

            <InfoAlert
              message={
                <>
                  <Typography variant="body1" gutterBottom>
                    To resolve this issue, please contact the Epta Service team and request the inclusion of the correct serial number in the system.
                    <br/>
                    <Box textAlign="center">
                      <Button
                        href="mailto:DigitalServicesOperation@eptarefrigeration.com"
                        startIcon={<MailOutlineIcon />}
                        sx={{
                          mt: 2,
                          backgroundColor: `#2C9AE1`,
                          color: `white`,
                          "&:hover": {
                            backgroundColor: `rgba(204, 204, 204,1.0)`,
                            color: `#000`,
                          },
                        }}
                      >
                        Contact Epta Service team 
                      </Button>
                    </Box>
                  </Typography>
                </>
              }
            />
          </>
        );
        break;

      case "api-error":
        content = (
          <>
            <Typography variant="h5" color="error" gutterBottom>
              <Tooltip title="Go back">
                <IconButton onClick={() => navigate(`/`)} sx={{ mr: 0.5 }}>
                  <ArrowBackIcon />
                </IconButton>
              </Tooltip>
              ⚠️ Service Error
            </Typography>
            <Typography variant="body1" gutterBottom>
              We’re sorry — there was a problem fetching the equipment data from our service.
            </Typography>
            <Typography variant="body1" sx={{ mt: 2 }}>
              Please try again later or contact the Epta Service team for support.
            </Typography>
          </>
        );
        break;

      case "invalid-data":
        content = (
          <>
            <Alert severity="info" icon={<InfoOutlined fontSize="inherit" sx={{ pt: "6px"}} />}>
                <Box sx={{ mt: 0}}>
                <Typography variant="h6" gutterBottom>
                  Missing Data
                </Typography>
                <Typography variant="body1" gutterBottom>
                  There is some information missing on asset {serialNumber.toUpperCase()}. Please verify the asset information or contact the Epta Service team and indicate that the serial number lacks information.
                  <br/>
                  <Box textAlign="center">
                    <Button
                      href="mailto:DigitalServicesOperation@eptarefrigeration.com"
                      startIcon={<MailOutlineIcon />}
                      sx={{
                        mt: 2,
                        backgroundColor: `#2C9AE1`,
                        color: `white`,
                        "&:hover": {
                          backgroundColor: `rgba(204, 204, 204,1.0)`,
                          color: `#000`,
                        },
                      }}
                    >
                      Contact Epta Service team 
                    </Button>
                  </Box>
                </Typography>
                </Box>
              </Alert>
          </>
        );
        break;

      case "missing-id":
        content = (
          <>
            <Alert severity="info" icon={<InfoOutlined fontSize="inherit" sx={{ pt: "6px"}} />}>
                <Box sx={{ mt: 0}}>
                <Typography variant="h6" gutterBottom>
                  <Tooltip title="Go back">
                    <IconButton onClick={() => navigate(`/`)} sx={{ mr: 0.5 }}>
                      <ArrowBackIcon />
                    </IconButton>
                  </Tooltip>
                  Missing Information
                </Typography>
                <Typography variant="body1" gutterBottom>
                  The asset {serialNumber.toUpperCase()} is missing information about the equipment. Please verify the asset information or contact the Epta Service team and indicate that the serial number lacks information.
                  <br/>
                  <Box textAlign="center">
                    <Button
                      href="mailto:DigitalServicesOperation@eptarefrigeration.com"
                      startIcon={<MailOutlineIcon />}
                      sx={{
                        mt: 2,
                        backgroundColor: `#2C9AE1`,
                        color: `white`,
                        "&:hover": {
                          backgroundColor: `rgba(204, 204, 204,1.0)`,
                          color: `#000`,
                        },
                      }}
                    >
                      Contact Epta Service team 
                    </Button>
                  </Box>
                </Typography>
                </Box>
              </Alert>
          </>
        );
        break;

      default:
        content = (
          <>
            <Typography variant="h5" color="error" gutterBottom>
              <Tooltip title="Go back">
                <IconButton onClick={() => navigate(`/`)} sx={{ mr: 0.5 }}>
                  <ArrowBackIcon />
                </IconButton>
              </Tooltip>
              ⚠️ Unknown Error
            </Typography>
            <Typography variant="body1" gutterBottom>
              An unexpected error has occurred. Please try again or contact support.
            </Typography>
          </>
        );
    }

    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          width: "100%",
          flexDirection: "column",
          backgroundImage: `linear-gradient(to bottom, rgba(215, 121, 0, 1), rgba(120, 40, 0, 1)) !important;`,
          // backgroundImage: `linear-gradient(to bottom, #172b4d, #172b4d) !important;`,
        }}
      >
        { error !== "invalid-data" && (
          <Card
            sx={{
              width: 600,
              p: 3,
              border: "1px solid",
              borderColor: "error.main",
              borderRadius: 2,
              bgcolor: "error.lighter",
              marginBottom: "1rem",
            }}
          >
            {content}
          </Card>
        )}

        { error === "invalid-data" && (
          <Box sx={{ marginTop: "1rem"}}>
            <Card 
              sx={{
                width: 650,
                borderRadius: 2,
                border: "1px solid",
                borderColor: "error.main",
                bgcolor: "error.lighter",
                marginBottom: "1rem",
              }}
            >
              <DialogTitle>
                <Tooltip title="Go back">
                  <IconButton onClick={() => navigate(`/`)} sx={{ mr: 0.5 }}>
                    <ArrowBackIcon />
                  </IconButton>
                </Tooltip>
                Equipment Details
              </DialogTitle>
              <Container
                sx={{
                  padding: "1rem",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-end",
                  justifyContent: "flex-end",
                }}
              >
                <TableContainer>
                  <Table size="small">
                    <TableBody>
                      { equipment.map((e) =>
                          tableData.map((row) =>
                            row.key !== "energy_label" && (
                              <TableRow key={row.key}>
                                <TableCell
                                  sx={{
                                    fontWeight: "bold",
                                    borderRight: `1px solid rgba(224, 224, 224, 1)`,
                                  }}
                                  component="th"
                                  scope="row"
                                  align="left"
                                  width="30%"
                                >
                                  {row.label}
                                </TableCell>
                                <TableCell align="left" width="70%">
                                  {equipment.map((e) => e[row.key])}
                                </TableCell>
                              </TableRow>
                            )
                          )
                        )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <>
                {equipment &&
                  equipment.length > 0 &&
                  equipment.map((row, key) =>
                  row.energy_label !== null ? (
                    <Button
                      sx={{
                        width: 'auto',
                        mt: 2, 
                        p: 1,
                        pl: 2,
                        display:"flex", 
                        justifyContent: "center", 
                        alignItems:"center",
                        border: "1px solid rgba(204, 204, 204,1.0)",
                        backgroundColor: 'rgba(204, 204, 204, 0.05)',
                        borderRadius: 2,
                        color: 'black',
                        shadowColor: 'rgba(0, 0, 0, 0.2)',
                        boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
                      }}
                      key={key}
                      variant="outlined"
                      target="_blank"
                      href={row["url"]}
                      endIcon={(() => {
                        switch (row["energy_label"]) {
                          case "A":
                            return (
                              <EnergyClassA
                                sx={{ width: 80, height: 40, ml: -1, p:1 }}
                              />
                            );
                          case "B":
                            return (
                              <EnergyClassB
                                sx={{ width: 80, height: 40, ml: -1 }}
                              />
                            );
                          case "C":
                            return (
                              <EnergyClassC
                                sx={{ width: 80, height: 40, ml: -1 }}
                              />
                            );
                          case "D":
                            return (
                              <EnergyClassD
                                sx={{ width: 80, height: 40, ml: -1 }}
                              />
                            );
                          case "E":
                            return (
                              <EnergyClassE
                                sx={{ width: 80, height: 40, ml: -1 }}
                              />
                            );
                          case "F":
                            return (
                              <EnergyClassF
                                sx={{ width: 80, height: 40, ml: -1 }}
                              />
                            );
                          case "G":
                            return (
                              <EnergyClassG
                                sx={{ width: 80, height: 40, ml: -1 }}
                              />
                            );
                          default:
                            return null;
                        }
                      })()}
                    >
                      <Typography sx={{fontWeight:"bold"}}>
                        Energy label
                      </Typography>
                    </Button>
                  ) : null
                )}
              </>
              </Container>
            </Card>
            {(() => {
              const nullFields = Object.keys(equipment[0]).filter(
                key => equipment[0][key] === null
              );
              const onlyStoreIdIsNull = nullFields.length === 1 && nullFields[0] === 'store_id';
              
              return !onlyStoreIdIsNull ? (
                <Card
                  sx={{
                    width: 600,
                    p: 3,
                    border: "1px solid",
                    borderColor: "error.main",
                    borderRadius: 2,
                    bgcolor: "error.lighter",
                    marginBottom: "1rem",
                  }}
                >
                  {content}
                </Card>
              ) : null;
            })()}
          </Box>
        )}
      </Box>
    );
  }

  return null;
}

export default Equipment;